import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { AlertCircle } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      const isDev = import.meta.env.DEV;
      return (
        <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-background text-foreground">
          <AlertCircle className="h-12 w-12 text-destructive mb-4" aria-hidden />
          <h1 className="text-xl font-semibold mb-2">Something went wrong</h1>
          <p className="text-muted-foreground text-center max-w-sm mb-6">
            An unexpected error occurred. Reload the page to try again.
          </p>
          {isDev && (
            <pre className="text-left w-full max-w-md text-xs text-muted-foreground bg-muted/50 p-4 rounded-lg mb-6 overflow-auto max-h-40">
              {this.state.error.message}
            </pre>
          )}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button onClick={this.handleReload}>Reload</Button>
            <a
              href={`mailto:support@luma-todo.xyz?subject=Luma%20Error%20Report&body=${encodeURIComponent(this.state.error.message)}`}
              className="inline-flex items-center justify-center rounded-md text-sm font-medium bg-secondary text-secondary-foreground hover:bg-secondary/80 h-10 px-4"
            >
              Report Issue
            </a>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
